import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { Station } from './models/station';

import { SystemDashboard } from './models/system-dashboard';
import { SystemDashboardApiParameter } from './models/system-dashboard-api-parameter';
import { SystemDashboardRawUser } from './models/system-dashboard-raw-user';
import { SystemDashboardRawVehicle } from './models/system-dashboard-raw-vehicle';
import { SystemDashboardRawTimeTable } from './models/system-dashboard-raw-time-table';
import { SystemDashboardRawTripPoint } from './models/system-dashboard-raw-trip-point';
import { SystemDashboardRawDispatchHistory } from './models/system-dashboard-raw-dispatch-history';

import { SystemDashboardUser } from './models/system-dashboard-user';
import { SystemDashboardUserPassenger } from './models/system-dashboard-user-passenger';
import { SystemDashboardUserPackage } from './models/system-dashboard-user-package';
import { SystemDashboardVehicle } from './models/system-dashboard-vehicle';
import { SystemDashboardTimeTable } from './models/system-dashboard-time-table';
import { SystemDashboardTripPoint } from './models/system-dashboard-trip-point';
import { SystemDashboardDispatchHistory } from './models/system-dashboard-dispatch-history';

import { ServerlessProvider } from './serverless';
import { SystemDashboardRaw } from './models/system-dashboard-raw';
import { GenericItem } from './models/generic-item';

import { UserStatusType } from '../../types/user-status-type';

import { JsonUtil } from '../../utilities/json-util';
import { TimeUtil } from '../../utilities/time-util';

/**
 * [システム] ダッシュボードのプロバイダー.
 */
@Injectable()
export class SystemDashboardServiceProvider {

  constructor(
    public serverless: ServerlessProvider
  ) { }

  /**
   * [システム]ダッシュボード取得処理.
   * 
   * @param parameter SystemDashboardApiParameter
   * @returns Observable<GenericItem<SystemDashboard>>
   */
  get(parameter: SystemDashboardApiParameter): Observable<GenericItem<SystemDashboard>> {
    return this.getRaw(parameter).map((res) => this.toSystemDashboard(res));
  }

  /**
   * [システム]ダッシュボードで取得したレスポンスデータの変換.
   * 
   * @param parameter SystemDashboardApiParamete
   * @returns Observable<GenericItem<SystemDashboardRaw>>
   */
  private getRaw(parameter: SystemDashboardApiParameter): Observable<GenericItem<SystemDashboardRaw>> {
    return this.serverless.postApi<SystemDashboardApiParameter,GenericItem<SystemDashboardRaw>>(
      '/v2/system/dashboard',parameter);
  }

  /**
   * GenericItem<SystemDashboard> の形式に変換します.
   * 
   * @param response 変換元のレスポンス
   * @returns 変換後のレスポンス
   */
  private toSystemDashboard(response: GenericItem<SystemDashboardRaw>): GenericItem<SystemDashboard> {
    const rawUsers = this.toRawUsers(response.item.users);
    const rawVehicles = this.toRawVehicles(response.item.vehicles);
    const rawTimeTables = this.toRawTimeTables(response.item.timeTables);
    const rawTripPoints = this.toRawTripPoints(response.item.tripPoints);
    const rawDispatchHistories = this.toRawDispatchHistories(response.item.dispatchHistories);

    const users = this.toUsers(rawUsers, response.item.imgUrlPrefix);
    const vehicles = this.toVehicles(rawVehicles, response.item.imgUrlPrefix, rawUsers, rawTimeTables);
    const timeTables = this.toTimeTables(rawTimeTables, response.item.imgUrlPrefix, rawUsers);
    const tripPoints = this.toTripPoints(rawTripPoints);
    const dispatchHistories = this.toDispatchHistories(rawDispatchHistories);

    return {
      result: response.result,
      message: response.message,
      item: {
        users: users,
        vehicles: vehicles,
        timeTables: timeTables,
        tripPoints: tripPoints,
        dispatchHistories: dispatchHistories,
        dispatchSuccessCount: response.item.dispatchSuccessCount,
        dispatchTotalCount: response.item.dispatchTotalCount,
        packageFlag: response.item.packageFlag
      }
    };
  }

  /**
   * SystemDashboardRawUser のリスト形式に変換します.
   *
   * @param fromList 変換元のリスト
   * @returns 変換後のリスト
   */
  private toRawUsers(fromList: any): SystemDashboardRawUser[] {
    const toList: SystemDashboardRawUser[] = [];

    fromList.forEach((from) => {
      toList[from[0]] = {
        id: from[0],
        areaId: from[1],
        userName: from[2],
        loginId: from[3],
        iconUrl: from[4],
        userStatus: from[5],
        lat: from[6],
        lon: from[7],
        homeStationCode: from[8],
        additionalInfo: JsonUtil.toObject(from[9])
      };
    });

    return toList;
  }

  /**
   * SystemDashboardRawVehicle のリスト形式に変換します.
   *
   * @param fromList 変換元のリスト
   * @returns 変換後のリスト
   */
  private toRawVehicles(fromList: any): SystemDashboardRawVehicle[] {
    const toList: SystemDashboardRawVehicle[] = [];

    fromList.forEach((from) => {
      toList[from[0]] = {
        id: from[0],
        areaId: from[1],
        vin: from[2],
        vehicleName: from[3],
        vehicleNumber: from[4],
        regionName: from[5],
        classificationNumber: from[6],
        identificationChar: from[7],
        sequentialNumber: from[8],
        vehicleModel: from[9],
        maxRideCount: from[10],
        maxCargoCount: from[11],
        sortOrder: from[12],
        waitPointCode: from[13],
        iconUrl: from[14],
        lat: from[15],
        lon: from[16],
        soc: from[17],
        doorLock: from[18],
        seatBelt: from[19],
        dir: from[20],
        taxiStatus: from[21],
        vehicleStatus: from[22],
        moderatorStatus: from[23],
        warning: from[24],
        adSysStatus: from[25],
        adStatus: from[26],
        rearLeftDoorOpen: from[27],
        StreamingName: from[28],
        vehicleIconUrl: from[29],
        enable: from[30],
        rideCount: from[31],
        adMode: from[32]
      };
    });

    return toList;
  }

  /**
   * SystemDashboardRawTimeTable のリスト形式に変換します.
   *
   * @param fromList 変換元のリスト
   * @returns 変換後のリスト
   */
  private toRawTimeTables(fromList: any): SystemDashboardRawTimeTable[] {
    const toList: SystemDashboardRawTimeTable[] = [];

    fromList.forEach((from) => {
      toList[from[0]] = {
        id: from[0],
        vehicleId: from[1],
        userId: from[2],
        rideCount: from[3],
        cargoCount: from[4],
        reserveStatus: from[5],
        requestFixedDt: from[6],
        pickUpPointCode: from[7],
        pickUpTrip: from[8],
        pickUpPlanDt: TimeUtil.toDate(from[9]),
        pickUpLimitDt: TimeUtil.toDate(from[10]),
        dropOffPointCode: from[11],
        dropOffTrip: from[12],
        dropOffPlanDt: TimeUtil.toDate(from[13]),
        dropOffLimitDt: TimeUtil.toDate(from[14]),
        vehicleArrivalPlanDt: TimeUtil.toDate(from[15]),
        vehicleArrivalDt: TimeUtil.toDate(from[16]),
        vehicleDeparturePlanDt: TimeUtil.toDate(from[17]),
        vehicleDepartureLimitDt: TimeUtil.toDate(from[18]),
        distance: from[19],
        travelTime: from[20],
        tourId: from[21],
        userReservationId: from[22],
        oosType: from[23],
        reserveType: from[24]
      };
    });

    return toList;
  }

  /**
   * SystemDashboardRawTripPoint のリスト形式に変換します.
   *
   * @param fromList 変換元のリスト
   * @returns 変換後のリスト
   */
  private toRawTripPoints(fromList: any): SystemDashboardRawTripPoint[] {
    const toList: SystemDashboardRawTripPoint[] = [];

    fromList.forEach((from) => {
      let tripPoint: SystemDashboardRawTripPoint = {
        id: from[0],
        vehicleId: from[1],
        trip: from[2],
        tripStatus: from[3],
        polyline: from[4],
        startPointCode: from[5],
        startPlanDt: TimeUtil.toDate(from[6]),
        endPointCode: from[7],
        endPlanDt: TimeUtil.toDate(from[8]),
        tourId: from[9],
        initialStartPlanDt: from[10],
        initialEndPlanDt: from[11]
      };
      toList.push(tripPoint);
    });

    return toList;
  }

  /**
   * SystemDashboardRawDispatchHistory のリスト形式に変換します.
   *
   * @param fromList 変換元のリスト
   * @returns 変換後のリスト
   */
  private toRawDispatchHistories(fromList: any): SystemDashboardRawDispatchHistory[] {
    const toList: SystemDashboardRawDispatchHistory[] = [];

    fromList.forEach((from) => {
      toList[from[0]] = {
        id: from[0],
        historyCreatedAt: TimeUtil.toDate(from[1]),
        vehicleId: from[2],
        userId: from[3],
        rideCount: from[4],
        cargoCount: from[5],
        requestFixedDt: TimeUtil.toDate(from[6]),
        pickUpPointCode: from[7],
        pickUpPlanDt: TimeUtil.toDate(from[8]),
        dropOffPointCode: from[9],
        dropOffPlanDt: TimeUtil.toDate(from[10]),
        beforeUserStatus: from[11],
        afterUserStatus: from[12],
        ownerId: from[13],
        cargoId: from[14]
      };
    });

    return toList;
  }

  /**
   * SystemDashboardRawUser のリスト形式に変換します.
   * 
   * @param rawUsers ユーザのリスト
   * @param imgUrlPrefix 画像URLプレフィックス
   * @returns 変換後のリスト
   */
  private toUsers(
    rawUsers: SystemDashboardRawUser[],
    imgUrlPrefix: string): SystemDashboardUser[] {
    const toList: SystemDashboardUser[] = [];

    rawUsers.forEach((rawUser) => {
      // 乗車ユーザの設定
      const toPassengerUser = this.getUser(rawUser.loginId, toList);

      toPassengerUser.passengers.push(this.toUserPassenger(rawUser, imgUrlPrefix));

      toList[rawUser.loginId] = toPassengerUser;

      // 荷物ユーザの設定
      const userPackages = this.toUserPackages(rawUser, imgUrlPrefix, rawUsers);

      userPackages.forEach((userPackage) => {
        const toCargoUser = this.getUser(userPackage.ownerId, toList);

        toCargoUser.packages.push(userPackage);

        toList[userPackage.ownerId] = toCargoUser;
      });
    });

    const convToList: SystemDashboardUser[] = [];

    for (const key in toList) {
      convToList.push(toList[key]);
    }

    return convToList;
  }

  /**
   * @param loginId ログインID
   * @param users ユーザ情報のリスト
   * @returns ユーザ情報
   */
  private getUser(loginId: string, users: SystemDashboardUser[]): SystemDashboardUser {
    const user = users[loginId];

    if (user) {
      return user;
    }

    return {
      loginId: loginId,
      passengers: [],
      packages: []
    };
  }

  /**
   * SystemDashboardUserPassenger の形式に変換します.
   * 
   * @param rawUser ユーザ
   * @param imgUrlPrefix 画像URLプレフィックス
   * @returns 変換後
   */
  private toUserPassenger(
    rawUser: SystemDashboardRawUser,
    imgUrlPrefix: string): SystemDashboardUserPassenger {
    if (!rawUser) {
      return null;
    }

    return {
      userId: rawUser.id,
      areaId: rawUser.areaId,
      loginId: rawUser.loginId,
      userName: rawUser.userName,
      iconUrl: imgUrlPrefix + rawUser.iconUrl,
      userStatus: rawUser.userStatus,
      lat: rawUser.lat,
      lon: rawUser.lon,
      homeStationCode: rawUser.homeStationCode,
      activeReservationId: null
    };
  }

  /**
   * SystemDashboardUserPackage のリスト形式に変換します.
   * 
   * @param rawUser ユーザ
   * @param imgUrlPrefix 画像URLプレフィックス
   * @param rawUsers ユーザのリスト
   * @returns 変換後のリスト
   */
  private toUserPackages(
    rawUser: SystemDashboardRawUser,
    imgUrlPrefix: string,
    rawUsers: SystemDashboardRawUser[]): SystemDashboardUserPackage[] {
    if (!rawUser || !rawUser.additionalInfo || !rawUser.additionalInfo.cargoes) {
      return [];
    }

    const toList: SystemDashboardUserPackage[] = [];

    rawUser.additionalInfo.cargoes.forEach((cargo) => {
      if (cargo && cargo.ownerId) {
        const cargoUser = this.findRawCargoUser(rawUsers, cargo.ownerId, rawUser.areaId);

        if (cargoUser) {
          toList.push(
            {
              userId: rawUser.id,
              areaId: cargoUser.areaId,
              userStatus: cargoUser.userStatus,
              lat: rawUser.lat,
              lon: rawUser.lon,
              ownerId: cargo.ownerId,
              cargoId: cargo.cargoId
            }
          );
        }
      }
    });

    return toList;
  }

  /**
   * 荷物ユーザを検索します.
   * 
   * @param rawUsers ユーザのリスト
   * @param loginId ログインID
   * @param areaId エリアID
   * @returns 荷物ユーザ
   */
  private findRawCargoUser(
    rawUsers: SystemDashboardRawUser[],
    loginId: string,
    areaId: number): SystemDashboardRawUser {
    for (const key in rawUsers) {
      const rawUser = rawUsers[key];
      if (rawUser.loginId === loginId && rawUser.areaId === areaId) {
        return rawUser;
      }
    }

    return null;
  }

  /**
   * SystemDashboardVehicle のリスト形式に変換します.
   *
   * @param rawVehicles 車両のリスト
   * @param imgUrlPrefix 画像URLプレフィックス
   * @param rawUsers ユーザのリスト
   * @param rawTimeTables 配車予約のリスト
   * @returns 変換後のリスト
   */
  private toVehicles(
    rawVehicles: SystemDashboardRawVehicle[],
    imgUrlPrefix: string,
    rawUsers: SystemDashboardRawUser[],
    rawTimeTables: SystemDashboardRawTimeTable[]): SystemDashboardVehicle[] {
    const toList: SystemDashboardVehicle[] = [];

    rawVehicles.forEach((rawVehicle) => {
      let rideCount = 0;
      let cargoCount = 0;
      let reserveStatus:boolean = false;

      rawTimeTables.forEach((rawTimeTable) => {
        if (rawTimeTable.vehicleId === rawVehicle.id) {
          const rawUser = rawUsers[rawTimeTable.userId];
          reserveStatus = true;

          if (rawUser.userStatus === UserStatusType.MOVING_DESTINATION
            || rawUser.userStatus === UserStatusType.ACCESS_IN_DROP_OFF_PLACE_NEAR
            || rawUser.userStatus === UserStatusType.WAITING_DROP_OFF) {
              if(!rawVehicle.adMode) {
                rideCount += rawTimeTable.rideCount;
              } else {
                rideCount = rawVehicle.rideCount;
              }
              cargoCount += rawTimeTable.cargoCount;
          }
        }
      })

      toList.push(({
        id: rawVehicle.id,
        areaId: rawVehicle.areaId,
        vin: rawVehicle.vin,
        vehicleName: rawVehicle.vehicleName,
        vehicleNumber: rawVehicle.vehicleNumber,
        vehicleModel: rawVehicle.vehicleModel,
        maxRideCount: rawVehicle.maxRideCount,
        maxCargoCount: rawVehicle.maxCargoCount,
        sortOrder: rawVehicle.sortOrder,
        waitPointCode: rawVehicle.waitPointCode,
        iconUrl: imgUrlPrefix + rawVehicle.iconUrl,
        vehicleImgUrl: imgUrlPrefix + rawVehicle.vehicleIconUrl,
        lat: rawVehicle.lat,
        lon: rawVehicle.lon,
        accuracy: 0,
        soc: rawVehicle.soc,
        doorLock: rawVehicle.doorLock,
        seatBelt: rawVehicle.seatBelt,
        dir: rawVehicle.dir,
        taxiStatus: rawVehicle.taxiStatus,
        vehicleStatus: rawVehicle.vehicleStatus,
        moderatorStatus: rawVehicle.moderatorStatus,
        warning: rawVehicle.warning,
        ad_sys_status: rawVehicle.adSysStatus,
        ad_status: rawVehicle.adStatus,
        rideCount: rideCount,
        cargoCount: cargoCount,
        reserveStatus: reserveStatus,
        rearLeftDoorOpen: rawVehicle.rearLeftDoorOpen,
        streamName: rawVehicle.StreamingName,
        enable: rawVehicle.enable,
        adMode : rawVehicle.adMode
      }));
    });

    return toList;
  }

  /**
   * SystemDashboardTimeTable のリスト形式に変換します.
   *
   * @param rawTimeTables 配車予約のリスト
   * @param imgUrlPrefix 画像URLプレフィックス
   * @param rawUsers ユーザのリスト
   * @returns 変換後のリスト
   */
  private toTimeTables(
    rawTimeTables: SystemDashboardRawTimeTable[],
    imgUrlPrefix: string,
    rawUsers: SystemDashboardRawUser[]): SystemDashboardTimeTable[] {
    const toList: SystemDashboardTimeTable[] = [];

    rawTimeTables.forEach((rawTimeTable) => {
      toList.push(({
        id: rawTimeTable.id,
        vehicleId: rawTimeTable.vehicleId,
        userId: rawTimeTable.userId,
        rideCount: rawTimeTable.rideCount,
        cargoCount: rawTimeTable.cargoCount,
        reserveStatus: rawTimeTable.reserveStatus,
        requestFixedDt: rawTimeTable.requestFixedDt,
        pickUpPointCode: rawTimeTable.pickUpPointCode,
        pickUpTrip: rawTimeTable.pickUpTrip,
        pickUpPlanDt: rawTimeTable.pickUpPlanDt,
        pickUpLimitDt: rawTimeTable.pickUpLimitDt,
        dropOffPointCode: rawTimeTable.dropOffPointCode,
        dropOffTrip: rawTimeTable.dropOffTrip,
        dropOffPlanDt: rawTimeTable.dropOffPlanDt,
        dropOffLimitDt: rawTimeTable.dropOffLimitDt,
        vehicleArrivalPlanDt: rawTimeTable.vehicleArrivalPlanDt,
        vehicleArrivalDt: rawTimeTable.vehicleArrivalDt,
        vehicleDeparturePlanDt: rawTimeTable.vehicleDeparturePlanDt,
        vehicleDepartureLimitDt: rawTimeTable.vehicleDepartureLimitDt,
        distance: rawTimeTable.distance,
        travelTime: rawTimeTable.travelTime,
        tourId: rawTimeTable.tourId,
        userReservationId: rawTimeTable.userReservationId,
        oosType: rawTimeTable.oosType,
        reserveType: rawTimeTable.reserveType,
        passenger: this.toUserPassenger(rawUsers[rawTimeTable.userId], imgUrlPrefix),
        packages: this.toUserPackages(rawUsers[rawTimeTable.userId], imgUrlPrefix, rawUsers)
      }));
    });

    return toList;
  }

  /**
   * SystemDashboardTripPoint のリスト形式に変換します.
   *
   * @param rawTripPoints トリップポイントのリスト
   * @returns 変換後のリスト
   */
  private toTripPoints(
    rawTripPoints: SystemDashboardRawTripPoint[]): SystemDashboardTripPoint[] {
    const toList: SystemDashboardTripPoint[] = [];

    rawTripPoints.forEach((rawTripPoint) => {
      toList.push(({
        id: rawTripPoint.id,
        vehicleId: rawTripPoint.vehicleId,
        trip: rawTripPoint.trip,
        tripStatus: rawTripPoint.tripStatus,
        polyline: rawTripPoint.polyline,
        startPointCode: rawTripPoint.startPointCode,
        startPlanDt: rawTripPoint.startPlanDt,
        endPointCode: rawTripPoint.endPointCode,
        endPlanDt: rawTripPoint.endPlanDt,
        tourId: rawTripPoint.tourId,
        initialStartPlanDt: rawTripPoint.initialStartPlanDt,
        initialEndPlanDt: rawTripPoint.initialEndPlanDt,
      }));
    });

    return toList;
  }

  /**
   * SystemDashboardDispatchHistory のリスト形式に変換します.
   *
   * @param rawDispatchHistories 配車履歴のリスト
   * @returns 変換後のリスト
   */
  private toDispatchHistories(
    rawDispatchHistories: SystemDashboardRawDispatchHistory[]): SystemDashboardDispatchHistory[] {
    const toList: SystemDashboardDispatchHistory[] = [];

    rawDispatchHistories.forEach((rawDispatchHistory) => {
      toList.push(({
        id: rawDispatchHistory.id,
        historyCreatedAt: rawDispatchHistory.historyCreatedAt,
        vehicleId: rawDispatchHistory.vehicleId,
        userId: rawDispatchHistory.userId,
        rideCount: rawDispatchHistory.rideCount,
        cargoCount: rawDispatchHistory.cargoCount,
        requestFixedDt: rawDispatchHistory.requestFixedDt,
        pickUpPointCode: rawDispatchHistory.pickUpPointCode,
        pickUpPlanDt: rawDispatchHistory.pickUpPlanDt,
        dropOffPointCode: rawDispatchHistory.dropOffPointCode,
        dropOffPlanDt: rawDispatchHistory.dropOffPlanDt,
        beforeUserStatus: rawDispatchHistory.beforeUserStatus,
        afterUserStatus: rawDispatchHistory.afterUserStatus,
        ownerId: rawDispatchHistory.ownerId,
        cargoId: rawDispatchHistory.cargoId
      }));
    });

    return toList;
  }

  /**
   * コードをキーにしたステーションのリストに変換します.
   *
   * @param stations ステーションのリスト
   * @returns コードをキーにしたステーションのリスト
   */
  toKeyByCodeStations(stations: Station[]): { [key: string]: Station; } {
    const toLost: { [key: string]: Station; } = {};

    stations.forEach((station) => {
      toLost[station.code] = station;
    });

    return toLost;
  }

  /**
   * 車両IDで集約した配車予約のリストに変換します.
   * 
   * @param tripPoints 配車予約のリスト
   * @returns 車両IDで集約した配車予約のリスト
   */
  toGroupingByVehicleIdTimeTables(timeTables: SystemDashboardTimeTable[]): { [key: string]: SystemDashboardTimeTable[]; } {
    const toLost: { [key: string]: SystemDashboardTimeTable[]; } = {};

    timeTables.forEach((timeTable) => {
      let to = toLost[timeTable.vehicleId];

      if (to) {
        to.push(timeTable);
      } else {
        to = [timeTable];
      }

      toLost[timeTable.vehicleId] = to;
    });

    return toLost;
  }

  /**
   * ユーザIDで集約した配車予約のリストに変換します.
   * 
   * @param tripPoints 配車予約のリスト
   * @returns ユーザIDで集約した配車予約のリスト
   */
  toGroupingByUserIdTimeTables(timeTables: SystemDashboardTimeTable[]): { [key: string]: SystemDashboardTimeTable[]; } {
    const toLost: { [key: string]: SystemDashboardTimeTable[]; } = {};

    timeTables.forEach((timeTable) => {
      let to = toLost[timeTable.userId];

      if (to) {
        to.push(timeTable);
      } else {
        to = [timeTable];
      }

      toLost[timeTable.userId] = to;
    });

    return toLost;
  }

  /**
   * 車両IDで集約したトリップポイントのリストに変換します.
   * 
   * @param tripPoints トリップポイントのリスト
   * @returns 車両IDで集約したトリップポイントのリスト
   */
  toGroupingByVehicleIdTripPoints(tripPoints: SystemDashboardTripPoint[]): { [key: string]: SystemDashboardTripPoint[]; } {
    const toLost: { [key: string]: SystemDashboardTripPoint[]; } = {};

    tripPoints.forEach((tripPoint) => {
      let to = toLost[tripPoint.vehicleId];

      if (to) {
        to.push(tripPoint);
      } else {
        to = [tripPoint];
      }

      toLost[tripPoint.vehicleId] = to;
    });

    return toLost;
  }

  /**
   * 車両IDで集約した車両のリストに変換します.
   * 
   * @param vehicles 車両のリスト
   * @returns 車両IDで集約した車両のリスト
   */
  toGroupingByVehicleIdVehicles(vehicles: SystemDashboardVehicle[]): { [key: string]: SystemDashboardVehicle; } {
    const toLost: { [key: string]: SystemDashboardVehicle; } = {};

    vehicles.forEach((vehicle) => {
      toLost[vehicle.id] = vehicle;
    });

    return toLost;
  }
}



// WEBPACK FOOTER //
// ./src/providers/servicer/system-dashboard-service.ts